import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../db';
import { Trophy, Dumbbell, Calendar, Clock, Star, ArrowLeft, UserPlus, UserCheck, UserX, History, Activity, Copy, Check, X, Settings as SettingsIcon, Mail } from 'lucide-react';
import { getLevelInfo, getGrade } from '../utils/xpUtils';
import { getEarnedBadges } from '../utils/badgeUtils';
import { toast, confirmDialog } from '../utils/feedback';
import Loader from '../components/Loader';
import { shopItems } from '../data/shopItems';
import Mascot from '../components/Mascot';
import UserAvatar from '../components/UserAvatar';
import SEO from '../components/SEO';
import { subscribeToProfileBossTitles } from '../services/bossService';

const PublicProfile = ({ currentProfile }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [clonedRoutines, setClonedRoutines] = useState({});
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [bossTitles, setBossTitles] = useState([]);

  useEffect(() => {
    const targetId = id || currentProfile?.id;
    if (!targetId) return;
    const unsubBossTitles = subscribeToProfileBossTitles(targetId, (titles) => {
      setBossTitles(titles);
    });
    return () => unsubBossTitles();
  }, [id, currentProfile?.id]);

  useEffect(() => {
    const loadProfileData = async () => {
      setLoading(true);
      const targetId = id || currentProfile?.id;
      if (!targetId) {
        setLoading(false);
        return;
      }
      let fetchedProfile = await db.getProfile(targetId);
      if (!fetchedProfile) {
        try {
          fetchedProfile = await db.getProfileByName(targetId);
        } catch (e) {
          // ignore
        }
      }
      if (fetchedProfile) {
        const resolvedId = fetchedProfile.id;
        setProfile(fetchedProfile);
        const [wData, rData, gData] = await Promise.all([
          db.getWorkouts(resolvedId),
          db.getRoutines(resolvedId),
          db.getGoals(resolvedId)
        ]);
        setWorkouts(wData);
        setRoutines(rData);
        setGoals(gData);
      }
      setLoading(false);
    };
    
    loadProfileData();
  }, [id, currentProfile]);

  if (loading) {
    return <Loader message="Chargement du profil..." />;
  }

  if (!profile) {
    return (
      <div className="card text-center" style={{ marginTop: '2rem' }}>
        <h2 style={{ color: 'var(--danger-color)' }}>Profil introuvable</h2>
        <p>Cet utilisateur n'existe pas ou a supprimé son compte.</p>
        <button className="btn btn-primary mt-2" onClick={() => navigate(-1)}>Retour</button>
      </div>
    );
  }

  const isMe = currentProfile?.id === profile.id;
  const isFriend = currentProfile?.friends?.includes(profile.id);
  const hasSentRequest = currentProfile?.sentRequests?.includes(profile.id);
  const hasReceivedRequest = currentProfile?.receivedRequests?.includes(profile.id);
  
  const xpInfo = getLevelInfo(profile.xp || 0);
  const grade = getGrade(xpInfo.level);
  const badges = getEarnedBadges(workouts, goals, profile);

  // Date de création : par défaut on met 10 mai 2026 si inconnu
  const joinDate = profile.createdAt 
    ? new Date(profile.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '10 mai 2026';

  const equippedTitle = shopItems.find(i => i.id === profile.equipped?.title);
  const equippedBorder = shopItems.find(i => i.id === profile.equipped?.border);
  const equippedMascot = profile.equipped?.mascot;

  const handleSendRequest = async () => {
    if(!currentProfile) return;
    await db.sendFriendRequest(currentProfile.id, profile.id);
    toast.success("Demande d'ami envoyée !");
  };

  const handleCancelRequest = async () => {
    if(!currentProfile) return;
    await db.cancelFriendRequest(currentProfile.id, profile.id);
    toast.success("Demande annulée.");
  };

  const handleRemoveFriend = async () => {
    if(!currentProfile) return;
    confirmDialog("Veux-tu vraiment retirer cet ami ?", async () => {
      await db.removeFriend(currentProfile.id, profile.id);
      toast.success("Ami retiré.");
    });
  };

  const handleCloneRoutine = async (routine) => {
    if (!currentProfile) return;
    try {
      const cloned = {
        ...routine,
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        name: `${routine.name} (Copié)`,
        profileId: currentProfile.id,
        isPublic: false
      };
      await db.addRoutine(cloned, currentProfile.id);
      setClonedRoutines(prev => ({ ...prev, [routine.id]: true }));
      toast.success("Programme cloné avec succès !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du clonage.");
    }
  };

  const formatDuration = (min) => {
    if (!min) return '';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h00`;
  };

  return (
    <div style={{ paddingBottom: '3rem', animation: 'fadeIn 0.3s ease-out' }}>
      <SEO title={profile.name ? `Profil de ${profile.name}` : 'Profil'} />
      <button className="btn btn-outline" style={{ padding: '0.4rem 0.8rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Retour
      </button>

      {/* HEADER PROFIL */}
      <div className="card" style={{ position: 'relative', overflow: 'hidden', padding: 0, marginBottom: '2rem' }}>
        <div style={{ height: '100px', background: profile.bannerGradient || 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))', opacity: 0.8 }}></div>
        
        {isMe && (
          <button 
            onClick={() => navigate('/settings')}
            style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 20, backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background-color 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.3)'}
          >
            <SettingsIcon size={20} />
          </button>
        )}

        <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '-60px', position: 'relative', zIndex: 10 }}>
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ marginBottom: '1rem', position: 'relative', zIndex: 2 }}>
              <UserAvatar 
                profile={profile} 
                size={120} 
                onClick={() => profile.avatarUrl && setShowAvatarModal(true)} 
              />
            </div>
            {equippedMascot && (
              <div style={{ position: 'absolute', bottom: '-10px', right: '-90px', zIndex: 3 }}>
                <Mascot id={equippedMascot} level={xpInfo.level} size={150} previewAccessory={profile.equipped?.mascot_accessory} />
              </div>
            )}
          </div>
          
          <h2 style={{ margin: 0, fontSize: '1.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {profile.name}
            {profile.isBetaTester && (
              <span 
                title="Pionnier / Bêta Testeur" 
                style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: 'rgba(162, 155, 254, 0.2)', 
                  border: '1px solid var(--primary-color)', 
                  color: 'var(--primary-color)', 
                  padding: '0.15rem 0.5rem', 
                  borderRadius: '12px', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  fontWeight: 'bold'
                }}
              >
                🎖️ Bêta Testeur
              </span>
            )}
          </h2>
          {bossTitles && bossTitles.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.45rem', marginBottom: '0.35rem' }}>
              {bossTitles.map(title => (
                <div 
                  key={title.id}
                  title="Titre de Prestige World Boss (actif jusqu'au prochain boss)" 
                  style={{ 
                    color: title.isKing ? '#fbbf24' : 'var(--primary-color-hover, #a89fcf)', 
                    fontSize: '0.95rem', 
                    fontWeight: 800, 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '0.35rem', 
                    backgroundColor: title.isKing ? 'rgba(251, 191, 36, 0.14)' : 'rgba(140, 127, 191, 0.16)', 
                    border: title.isKing ? '1.5px solid #fbbf24' : '1.5px solid var(--primary-color, #8c7fbf)', 
                    padding: '0.2rem 0.75rem', 
                    borderRadius: '16px', 
                    boxShadow: title.isKing ? '0 0 12px rgba(251, 191, 36, 0.25)' : '0 0 12px rgba(140, 127, 191, 0.25)' 
                  }}
                >
                  {title.name}
                </div>
              ))}
            </div>
          )}
          {equippedTitle && <div style={{ color: 'var(--primary-color)', fontSize: '1rem', marginTop: '0.3rem', marginBottom: '0.35rem', fontWeight: 'bold' }}>{equippedTitle.value}</div>}
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', marginTop: '0.55rem', lineHeight: 1 }}>
            <Calendar size={14} style={{ position: 'relative', top: '-1px' }} /> <span>Membre depuis le {joinDate}</span>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', backgroundColor: 'var(--bg-card)', padding: '0.8rem 1.2rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', flex: 1, maxWidth: '120px' }}>
              <div style={{ color: 'var(--secondary-color)', fontWeight: 'bold', fontSize: '1.4rem' }}>{xpInfo.level}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Niveau</div>
            </div>
            <div style={{ textAlign: 'center', backgroundColor: 'var(--bg-card)', padding: '0.8rem 1.2rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', flex: 1, maxWidth: '120px' }}>
              <div style={{ color: 'var(--primary-color)', fontWeight: 'bold', fontSize: '1.4rem' }}>{profile.xp || 0}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>XP Total</div>
            </div>
            <div style={{ textAlign: 'center', backgroundColor: 'var(--bg-card)', padding: '0.8rem 1.2rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', flex: 1, maxWidth: '120px' }}>
              <div style={{ color: '#FFD700', fontWeight: 'bold', fontSize: '1.4rem' }}>{workouts.length}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Séances</div>
            </div>
          </div>

          <div style={{ marginTop: '1rem', color: 'var(--text-primary)', fontWeight: 'bold' }}>
            Rang : {grade} (Niveau {xpInfo.level})
          </div>

          {!isMe && (
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', width: '100%', maxWidth: '300px' }}>
              {isFriend ? (
                <>
                  <button className="btn btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }} onClick={() => navigate('/messages?user=' + profile.id)}>
                    <Mail size={18} /> Message
                  </button>
                  <button className="btn" style={{ flex: 1, backgroundColor: 'rgba(231, 76, 60, 0.1)', color: 'var(--danger-color)', border: '1px solid var(--danger-color)', padding: '0.8rem' }} onClick={handleRemoveFriend}>
                    <UserX size={18} /> Retirer
                  </button>
                </>
              ) : hasReceivedRequest ? (
                <button className="btn btn-outline" style={{ flex: 1, padding: '0.8rem' }} onClick={() => navigate('/social?tab=requests')}>
                  <UserCheck size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Répondre à la demande
                </button>
              ) : hasSentRequest ? (
                <button className="btn btn-outline" style={{ flex: 1, padding: '0.8rem' }} onClick={handleCancelRequest}>
                  <Clock size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Demande en attente
                </button>
              ) : (
                <button className="btn btn-primary" style={{ flex: 1, padding: '0.8rem' }} onClick={handleSendRequest}>
                  <UserPlus size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Ajouter en ami
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* BADGES */}
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', marginTop: '2rem' }}><Trophy size={20} color="var(--primary-color)"/> Badges ({badges.length})</h3>
      {badges.length === 0 ? (
        <div className="card text-center text-secondary" style={{ marginBottom: '2rem' }}>Aucun badge obtenu pour le moment.</div>
      ) : (
        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
          {badges.map(badge => {
            const isBeta = badge.id === 'beta_tester';
            return (
              <div 
                key={badge.id} 
                title={badge.description} 
                style={{ 
                  backgroundColor: 'var(--bg-card)', 
                  padding: '0.6rem 1rem', 
                  borderRadius: '20px', 
                  border: isBeta ? '2px solid var(--primary-color)' : '1px solid var(--primary-color)', 
                  fontSize: '0.9rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  boxShadow: isBeta ? '0 0 12px rgba(162, 155, 254, 0.4)' : '0 2px 8px rgba(162, 155, 254, 0.15)' 
                }}
              >
                {badge.name}
              </div>
            );
          })}
        </div>
      )}

      {/* PROGRAMMES */}
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', marginTop: '2rem' }}><Dumbbell size={20} color="var(--primary-color)"/> Programmes ({routines.length})</h3>
      {routines.length === 0 ? (
        <div className="card text-center text-secondary" style={{ marginBottom: '2rem' }}>Aucun programme partagé.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {routines.map(routine => (
            <div 
              key={routine.id} 
              className="card" 
              style={{ padding: '1rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: 'flex-start', cursor: 'pointer', transition: 'transform 0.1s' }}
              onClick={() => setSelectedRoutine({ ...routine, isMe })}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <strong style={{ fontSize: '0.95rem' }}>{routine.name}</strong>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{routine.exercises?.length || 0} exercices</div>
              
              {!isMe && currentProfile && (
                <button 
                  className="btn" 
                  style={{ padding: '0.3rem', width: '100%', fontSize: '0.75rem', display: 'flex', gap: '0.3rem', justifyContent: 'center', backgroundColor: clonedRoutines[routine.id] ? 'var(--secondary-color)' : 'var(--primary-color)', color: clonedRoutines[routine.id] ? '#121016' : '#fff', marginTop: 'auto' }}
                  onClick={(e) => { e.stopPropagation(); handleCloneRoutine(routine); }}
                  disabled={clonedRoutines[routine.id]}
                >
                  {clonedRoutines[routine.id] ? <Check size={12} /> : <Copy size={12} />}
                  {clonedRoutines[routine.id] ? 'Cloné' : 'Cloner'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ACTIVITÉ RÉCENTE */}
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', marginTop: '2rem' }}><Activity size={20} color="var(--primary-color)"/> Activité Récente</h3>
      {workouts.length === 0 ? (
        <div className="card text-center text-secondary">Aucune séance terminée.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {workouts.slice(0, 5).map(workout => {
            const date = new Date(workout.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
            return (
              <div key={workout.id} className="card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{workout.routineName || workout.name || (workout.muscles?.length > 0 ? `Séance ${workout.muscles.join(', ')}` : 'Entraînement')}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{date} {workout.duration ? `• ${formatDuration(workout.duration)}` : ''}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Routine Details Modal */}
      {selectedRoutine && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedRoutine(null)}>
          <div className="card" style={{ width: '90%', maxWidth: '400px', maxHeight: '80vh', overflow: 'hidden', padding: '1.5rem', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4" style={{ flexShrink: 0 }}>
              <h3 style={{ margin: 0 }}>{selectedRoutine.name}</h3>
              <button className="btn-icon" onClick={() => setSelectedRoutine(null)}><X size={24} /></button>
            </div>
            
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', flexShrink: 0 }}>Exercices</h4>
            {selectedRoutine.exercises && selectedRoutine.exercises.length > 0 ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginBottom: '1rem', paddingRight: '0.5rem' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedRoutine.exercises.map((ex, i) => {
                    if (ex.type === 'superset') {
                      return (
                        <li key={i} style={{ padding: '0.8rem', backgroundColor: 'rgba(168, 159, 207, 0.05)', borderRadius: 'var(--border-radius-sm)', border: '1px dashed var(--primary-color)' }}>
                          <div style={{ fontSize: '0.7rem', color: 'var(--primary-color)', marginBottom: '0.3rem', fontWeight: 'bold' }}>SUPERSET</div>
                          {ex.items && ex.items.map((subEx, j) => (
                            <strong key={j} style={{ display: 'block', fontSize: '0.95rem', marginTop: j > 0 ? '0.5rem' : 0 }}>
                              {subEx.name}
                            </strong>
                          ))}
                        </li>
                      );
                    }
                    return (
                      <li key={i} style={{ padding: '0.8rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)' }}>
                        <strong style={{ display: 'block', fontSize: '0.95rem' }}>{ex.name}</strong>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center text-secondary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Aucun exercice dans ce programme.</div>
            )}

            {!selectedRoutine.isMe && currentProfile && (
              <button 
                className="btn btn-primary" 
                style={{ width: '100%', marginTop: 'auto', display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '0.8rem', flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); handleCloneRoutine(selectedRoutine); setSelectedRoutine(null); }}
                disabled={clonedRoutines[selectedRoutine.id]}
              >
                {clonedRoutines[selectedRoutine.id] ? <Check size={18} /> : <Copy size={18} />}
                {clonedRoutines[selectedRoutine.id] ? 'Programme Cloné' : 'Cloner ce programme'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Avatar Modal */}
      {showAvatarModal && profile.avatarUrl && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setShowAvatarModal(false)}>
          <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }} onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowAvatarModal(false)}
              style={{ position: 'absolute', top: '-40px', right: '0', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={32} />
            </button>
            <img loading="lazy" src={profile.avatarUrl} alt={profile.name} style={{ width: '400px', height: '400px', maxWidth: '90vw', maxHeight: '90vw', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} />
          </div>
        </div>
      )}

    </div>
  );
};

export default PublicProfile;
